import { DateTime } from "luxon";

import { TruckIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";

import {
  IProductionDashboardBay,
  ProductionDashboardBayStatus,
} from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import {
  complexityChipClasses,
  complexityLabel,
  lineChipClasses,
  lineLabel,
} from "../../utils/vehicleDisplay";
import AssemblyTimer from "./AssemblyTimer";
import Avatar from "../user/Avatar";
import { useSignalR } from "../../contexts/SignalRContext";

interface BayStationCardProps {
  bay: IProductionDashboardBay;
  now: DateTime;
}

const statusLabel = (status: ProductionDashboardBayStatus): string => {
  switch (status) {
    case ProductionDashboardBayStatus.InProgress:
      return "In progress";
    case ProductionDashboardBayStatus.Complete:
      return "Complete";
    default:
      return "Available";
  }
};

const statusChip = (status: ProductionDashboardBayStatus): string => {
  switch (status) {
    case ProductionDashboardBayStatus.InProgress:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    case ProductionDashboardBayStatus.Complete:
      return "bg-blue-50 text-blue-700 border border-blue-200";
    default:
      return "bg-gray-50 text-gray-600 border border-gray-200";
  }
};

const progressBarColor = (status: ProductionDashboardBayStatus): string => {
  switch (status) {
    case ProductionDashboardBayStatus.Complete:
      return "bg-blue-500";
    case ProductionDashboardBayStatus.InProgress:
      return "bg-emerald-500";
    default:
      return "bg-gray-300";
  }
};

export default function BayStationCard({ bay, now }: BayStationCardProps) {
  const { currentVehicle } = bay;
  const { activeJobSessionsByVehicle } = useSignalR();
  const activeJobUsers = currentVehicle ? (activeJobSessionsByVehicle[currentVehicle.id] ?? []) : [];

  const handleOpenVehicle = () => {
    if (!currentVehicle) return;
    openInNewTab(`/vehicles/${currentVehicle.id}`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 min-w-[220px] flex-1 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-900 truncate">{bay.name}</div>
        <span
          className={classNames(
            "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5",
            statusChip(bay.status)
          )}
        >
          {statusLabel(bay.status)}
        </span>
      </div>

      {!currentVehicle ? (
        <div className="mt-6 mb-6 flex-1 flex items-center justify-center text-xs text-gray-400 italic">
          No vehicle on station
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {currentVehicle.vrm || "No VRM"}
              </div>
              <div className="text-xs text-gray-700 truncate">
                {[currentVehicle.combinedMake, currentVehicle.combinedModel]
                  .filter(Boolean)
                  .join(" ")}
                {currentVehicle.bodyStyle && (
                  <span className="ml-1 text-gray-500">{currentVehicle.bodyStyle}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleOpenVehicle}
              className="inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              title="Open vehicle"
            >
              <TruckIcon className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span
              className={classNames(
                "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5",
                complexityChipClasses(currentVehicle.complexity)
              )}
            >
              {complexityLabel(currentVehicle.complexity)}
            </span>
            <span
              className={classNames(
                "text-[10px] font-medium rounded px-1.5 py-0.5",
                lineChipClasses(currentVehicle.productionLine)
              )}
            >
              {lineLabel(currentVehicle.productionLine)}
            </span>
          </div>

          {activeJobUsers.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1">
              <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <div className="flex">
                {activeJobUsers.map((userId, key) => (
                  <Avatar key={key} userId={userId} classes="w-6 h-6 rounded-full [&:not(:first-child)]:-ml-1.5" textClasses="text-[15px]" />
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex-1">
            <div className="flex items-center justify-between text-[11px] text-gray-600">
              <span className="font-semibold">{currentVehicle.percentComplete}%</span>
              <span className="text-gray-500">
                {currentVehicle.partsRemoved}/{currentVehicle.partsTotal} parts
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full rounded bg-gray-100 overflow-hidden">
              <div
                className={classNames("h-full", progressBarColor(bay.status))}
                style={{ width: `${Math.min(100, currentVehicle.percentComplete)}%` }}
              />
            </div>
            {currentVehicle.assemblyCount > 0 && currentVehicle.currentAssemblyName && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="text-[11px] text-gray-500 truncate">
                  Assembly {currentVehicle.currentAssemblyIndex} of {currentVehicle.assemblyCount}:{" "}
                  <span className="text-gray-700">{currentVehicle.currentAssemblyName}</span>
                </div>
                <AssemblyTimer vehicle={currentVehicle} now={now} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
